<template>
  <div class="popup z-index-10-fg confirmation small-popup quote-product-popup">
    <div class="popup-content">
      <div class="popup-header">
        <h2>{{ isNew ? 'Add Quote Product' : 'Edit Quote Product' }}</h2>
        <img src="../../assets/close.svg" alt="" class="popup-close" @click="$emit('close')" />
      </div>

      <div class="popup-body">
        <div class="product-fields">
          <TextBox label="Part #" v-model="localProduct.PARTNUM" :invalid="showErrors && !localProduct.PARTNUM" required />
          <TextBox label="Old Part #" v-model="localProduct.OLDPARTNUM" />
          <TextBox label="Description" v-model="localProduct.DESCRIPTION" />
          <TextBox label="Color" v-model="localProduct.COLOR" />
          <Dropdown label="UOM" v-model="localProduct.UOM" :options="['EA', 'BND', 'FT2']" />
          <TextBox label="Weight" v-model="localProduct.WEIGHT" />
          <TextBox label="Pieces / FT2" v-model="localProduct.PIECESPERSQFT" />
        </div>

        <h3>Pricing</h3>
        <table class="full-width-table pricing-table">
          <tbody>
            <tr class="table-header">
              <th>Pricing Structure</th>
              <th>Price / {{ localProduct.UOM || 'EA' }}</th>
              <th>Alternate / FT2</th>
            </tr>
            <tr v-for="price in localPrices" :key="price.PRICELISTID">
              <td>{{ price.name }}</td>
              <td class="table-input"><input type="text" inputmode="decimal" v-model="price.PRICEPC" /></td>
              <td v-if="localProduct.UOM !== 'FT2'" class="table-input"><input type="text" inputmode="decimal" v-model="price.PRICESQFT" /></td>
              <td v-else>—</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="popup-footer">
        <button v-if="!isNew" class="text-btn red" @click="showDeleteConfirm = true">Delete</button>
        <span class="fill"></span>
        <button @click="$emit('close')" class="text-btn">cancel</button>
        <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="saveProduct()">Save<img src="../../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
  <Confirmation
    v-if="showDeleteConfirm"
    header="Delete Quote Product?"
    :message="`Are you sure you want to delete ${localProduct.PARTNUM}?`"
    confirmText="Delete"
    cancelText="Cancel"
    serious
    @confirm="deleteProduct()"
    @cancel="showDeleteConfirm = false" />
</template>

<script>
import TextBox from '@/components/inputs/TextBox.vue';
import Dropdown from '@/components/inputs/Dropdown.vue';
import Confirmation from '@/components/Confirmation.vue';
import useQuoteProductMutate from '@/composables/useQuoteProductMutate';
import useAlerts from '@/composables/useAlerts';

export default {
  name: 'EditQuoteProduct',
  components: { TextBox, Dropdown, Confirmation },
  emits: ['close', 'saved'],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    priceLists: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { addQuoteProduct, updateQuoteProduct, deleteQuoteProduct } = useQuoteProductMutate();
    const { addAlert } = useAlerts();

    return {
      addQuoteProduct,
      updateQuoteProduct,
      deleteQuoteProduct,
      addAlert,
    };
  },
  data() {
    return {
      localProduct: {},
      localPrices: [],
      isSaving: false,
      showErrors: false,
      showDeleteConfirm: false,
    };
  },
  computed: {
    isNew() {
      return !this.product?.PRODUCTID;
    },
  },
  methods: {
    loadProduct() {
      this.localProduct = {
        PRODUCTID: this.product?.PRODUCTID || null,
        PARTNUM: this.product?.PARTNUM || '',
        OLDPARTNUM: this.product?.OLDPARTNUM || '',
        DESCRIPTION: this.product?.DESCRIPTION || '',
        COLOR: this.product?.COLOR || '',
        UOM: this.product?.UOM || 'EA',
        WEIGHT: this.product?.WEIGHT ?? '',
        PIECESPERSQFT: this.product?.PIECESPERSQFT ?? '',
      };

      this.localPrices = this.priceLists.map((priceList) => {
        const existingPrice = this.product?.prices?.find((price) => price.PRICELISTID === priceList.PICKLISTITEMID);
        return {
          name: priceList.NAME,
          PRICELISTID: priceList.PICKLISTITEMID,
          PRICEPC: existingPrice?.PRICEPC ?? '',
          PRICESQFT: existingPrice?.PRICESQFT ?? '',
        };
      });
    },
    normalizeNumber(value) {
      if (value === '' || value === null || value === undefined) return null;
      return Number(value);
    },
    buildPayload() {
      return {
        PARTNUM: this.localProduct.PARTNUM,
        OLDPARTNUM: this.localProduct.OLDPARTNUM,
        DESCRIPTION: this.localProduct.DESCRIPTION,
        COLOR: this.localProduct.COLOR,
        UOM: this.localProduct.UOM || 'EA',
        WEIGHT: this.normalizeNumber(this.localProduct.WEIGHT),
        PIECESPERSQFT: this.normalizeNumber(this.localProduct.PIECESPERSQFT),
        prices: this.localPrices.map((price) => ({
          PRICELISTID: price.PRICELISTID,
          PRICEPC: this.normalizeNumber(price.PRICEPC),
          PRICESQFT: this.normalizeNumber(price.PRICESQFT),
        })),
      };
    },
    async saveProduct() {
      this.showErrors = true;
      if (!this.localProduct.PARTNUM) {
        this.addAlert('Part # is required', 'high', 3);
        return;
      }

      this.isSaving = true;
      const payload = this.buildPayload();
      const result = this.isNew ? await this.addQuoteProduct(payload) : await this.updateQuoteProduct(this.localProduct.PRODUCTID, payload);
      this.isSaving = false;

      if (!result) return;

      this.addAlert('Quote product saved', 'low', 3);
      this.$emit('saved');
    },
    async deleteProduct() {
      this.isSaving = true;
      const result = await this.deleteQuoteProduct(this.localProduct.PRODUCTID);
      this.isSaving = false;

      if (!result) return;

      this.addAlert('Quote product deleted', 'low', 3);
      this.$emit('saved');
    },
  },
  mounted() {
    this.loadProduct();
  },
};
</script>

<style lang="scss" scoped>
.quote-product-popup.small-popup {
  width: 58rem;
  max-width: 80vw;

  .product-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: $fontSize16;
    font-weight: 500;
    margin: 0 0 0.5rem;
  }

  .pricing-table {
    min-width: 0;
  }

  .fill {
    flex: 1;
  }
}
</style>
